import { useCallback, useEffect, useState } from "react";
import type { DeleteAccountPayload, Profile, ProfileImageKind, UpdatePasswordPayload, UpdateProfilePayload } from "api";
import { profileApi } from "api";
import { getErrorMessage, isForbidden, isUnauthenticated } from "utils/error.utils";

export type LoadStatus = "loading" | "ready" | "unauthorized" | "unverified" | "failed";

type UseProfile = {
  profile: Profile | null;
  status: LoadStatus;
  save: (payload: UpdateProfilePayload) => Promise<string | null>;
  uploadImage: (kind: ProfileImageKind, file: File) => Promise<string | null>;
  removeImage: (kind: ProfileImageKind) => Promise<string | null>;
  changePassword: (payload: UpdatePasswordPayload) => Promise<string | null>;
  deleteAccount: (payload: DeleteAccountPayload) => Promise<string | null>;
};

/**
 * The profile doubles as the session check: the cabinet asks for it first and
 * every pane below assumes the answer.
 *
 * The mutations resolve to an error message, or to null when they succeeded —
 * the forms show it inline rather than throwing anything at the user.
 */
export const useProfile = (onSessionLost: () => void): UseProfile => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");

  useEffect(() => {
    let cancelled = false;

    profileApi
      .show()
      .then(({ data }) => {
        if (cancelled) return;
        setProfile(data.data);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          setStatus("unauthorized");
          return;
        }
        setStatus(isForbidden(error) ? "unverified" : "failed");
      });

    return () => {
      cancelled = true;
    };
  }, [onSessionLost]);

  const save = useCallback(
    async (payload: UpdateProfilePayload): Promise<string | null> => {
      try {
        const { data } = await profileApi.update(payload);
        setProfile(data.data);

        return null;
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        }

        return getErrorMessage(error, "Could not save your profile. Please try again.");
      }
    },
    [onSessionLost]
  );

  /**
   * Both image routes answer with the whole profile, so one response repaints
   * the card. A rejected upload changes nothing: the old picture stays and the
   * message goes back to the form.
   */
  const uploadImage = useCallback(
    async (kind: ProfileImageKind, file: File): Promise<string | null> => {
      try {
        const { data } = await profileApi.uploadImage(kind, file);
        setProfile(data.data);

        return null;
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        }

        return getErrorMessage(error, "Could not upload the image. Please try again.");
      }
    },
    [onSessionLost]
  );

  const removeImage = useCallback(
    async (kind: ProfileImageKind): Promise<string | null> => {
      try {
        const { data } = await profileApi.removeImage(kind);
        setProfile(data.data);

        return null;
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        }

        return getErrorMessage(error, "Could not remove the image. Please try again.");
      }
    },
    [onSessionLost]
  );

  const changePassword = useCallback(
    async (payload: UpdatePasswordPayload): Promise<string | null> => {
      try {
        await profileApi.updatePassword(payload);

        return null;
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        }

        return getErrorMessage(error, "Could not update your password. Please try again.");
      }
    },
    [onSessionLost]
  );

  const deleteAccount = useCallback(async (payload: DeleteAccountPayload): Promise<string | null> => {
    try {
      await profileApi.destroy(payload);

      return null;
    } catch (error) {
      return getErrorMessage(error, "Could not delete the account. Please try again.");
    }
  }, []);

  return { profile, status, save, uploadImage, removeImage, changePassword, deleteAccount };
};
